import * as React from "react"
import { useEffect } from "react"
import { useSignal } from "@preact/signals-react"
import {
    Box,
    Grid,
    Typography,
    Avatar,
    Alert,
    Divider
} from "@mui/material"
import { object, number, string } from "yup"
import { DrawerSeeker, AutocompleteAsyncSeeker, TextFieldSeeker, IconSeeker, SelectSeeker } from "@ui-seeker/core"
import { useValidatorSeeker } from "@ui-seeker/app"
import { isEmpty } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { createSkuDrawer, selectedRows, tableData } from "../@signals"


export default function CreateSkuDrawer() {
    const drawerData = createSkuDrawer.value
    const isOpen = Boolean(drawerData)

    const skuFormData = useSignal({})
    const isSubmitting = useSignal(false)
    const submitError = useSignal(null)
    const createNewLine = useSignal(false)

    const candidates = drawerData?.candidates || []

    // Inference helpers
    const inferFromText = (text) => {
        if (!text) return {}

        const textLower = text.toLowerCase()
        const result = {}

        // Infer size and unity - patterns like "100ml", "50 g", "1.5L", "250gr"
        const sizePatterns = [
            /(\d+(?:[.,]\d+)?)\s*(ml|l|cl|g|gr|kg|mg|oz|fl\.?\s*oz|unidades?|ud|uds|capsulas?|caps?|tabletas?|tabs?|comprimidos?|sobres?|ampollas?|sticks?)/gi
        ]
        for (const pattern of sizePatterns) {
            const match = text.match(pattern)
            if (match) {
                const fullMatch = match[0]
                const numMatch = fullMatch.match(/(\d+(?:[.,]\d+)?)/)
                const unitMatch = fullMatch.match(/(ml|l|cl|g|gr|kg|mg|oz|fl\.?\s*oz|unidades?|ud|uds|capsulas?|caps?|tabletas?|tabs?|comprimidos?|sobres?|ampollas?|sticks?)/i)
                if (numMatch && unitMatch) {
                    result.size = parseFloat(numMatch[1].replace(',', '.'))
                    let unit = unitMatch[1].toLowerCase()
                    // Normalize units
                    if (unit === 'gr') unit = 'g'
                    if (unit.match(/^(unidades?|ud|uds)$/)) unit = 'ud'
                    if (unit.match(/^(capsulas?|caps?)$/)) unit = 'caps'
                    if (unit.match(/^(tabletas?|tabs?|comprimidos?)$/)) unit = 'tabs'
                    result.unitySize = unit.substring(0, 5) // Max 5 chars
                }
                break
            }
        }

        // Infer color
        const colors = {
            // Spanish
            'negro': 'Negro', 'negra': 'Negro', 'black': 'Negro',
            'blanco': 'Blanco', 'blanca': 'Blanco', 'white': 'Blanco',
            'rojo': 'Rojo', 'roja': 'Rojo', 'red': 'Rojo',
            'azul': 'Azul', 'blue': 'Azul',
            'verde': 'Verde', 'green': 'Verde',
            'amarillo': 'Amarillo', 'amarilla': 'Amarillo', 'yellow': 'Amarillo',
            'naranja': 'Naranja', 'orange': 'Naranja',
            'rosa': 'Rosa', 'rosado': 'Rosa', 'pink': 'Rosa',
            'morado': 'Morado', 'morada': 'Morado', 'purple': 'Morado', 'violeta': 'Morado',
            'gris': 'Gris', 'gray': 'Gris', 'grey': 'Gris',
            'marrón': 'Marrón', 'marron': 'Marrón', 'brown': 'Marrón', 'café': 'Marrón',
            'beige': 'Beige', 'beis': 'Beige',
            'dorado': 'Dorado', 'dorada': 'Dorado', 'gold': 'Dorado', 'golden': 'Dorado',
            'plateado': 'Plateado', 'plateada': 'Plateado', 'silver': 'Plateado',
            'nude': 'Nude', 'coral': 'Coral', 'burgundy': 'Burgundy', 'borgoña': 'Burgundy'
        }
        for (const [key, value] of Object.entries(colors)) {
            // Match whole word only
            const regex = new RegExp(`\\b${key}\\b`, 'i')
            if (regex.test(textLower)) {
                result.color = value
                break
            }
        }

        // Infer gender (1=Female, 2=Male, 3=Unisex)
        const genderPatterns = {
            2: /\b(hombre|hombres|man|men|masculino|caballero|him|homme|pour homme|for him|mens?)\b/i,
            1: /\b(mujer|mujeres|woman|women|femenino|dama|her|femme|pour femme|for her|womens?|lady|ladies)\b/i,
            3: /\b(unisex|mixto|universal|niño|niña|niños|niñas|boy|girl|boys|girls|child|children|kids?|infantil|junior|jr)\b/i
        }
        for (const [gender, pattern] of Object.entries(genderPatterns)) {
            if (pattern.test(text)) {
                result.gender = parseInt(gender)
                break
            }
        }

        return result
    }

    // Generate SKU name from Brand + Title + variant_raw (if not in title)
    const generateSkuName = () => {
        const brandName = drawerData?.prefill?.brand_name || ''
        const firstRaw = candidates[0]?.raw
        const title = firstRaw?.title || ''
        const variantRaw = firstRaw?.variant_raw || ''

        let skuName = brandName ? `${brandName} ${title}` : title

        // Add variant_raw only if it's not already contained in the title
        if (variantRaw && !title.toLowerCase().includes(variantRaw.toLowerCase())) {
            skuName = `${skuName} ${variantRaw}`
        }

        return skuName.trim()
    }

    // Reset form when drawer opens
    useEffect(() => {
        if (drawerData) {
            const firstRaw = candidates[0]?.raw
            const combinedText = `${firstRaw?.title || ''} ${firstRaw?.variant_raw || ''} ${firstRaw?.subtitle || ''}`
            const inferred = inferFromText(combinedText)

            skuFormData.value = {
                brandID: drawerData.prefill?.id_brand || null,
                lineID: null,
                lineName: '',
                skuName: generateSkuName(),
                ean: firstRaw?.ean || '',
                reference: firstRaw?.reference || '',
                categoryID: null,
                customerIDs: [],
                // New fields with inference
                size: inferred.size || '',
                unitySize: inferred.unitySize || '',
                color: inferred.color || '',
                gender: inferred.gender || null
            }
            submitError.value = null
            createNewLine.value = false
        }
    }, [drawerData])

    const validatorData = useValidatorSeeker({
        brandID: skuFormData.value.brandID,
        skuName: skuFormData.value.skuName
    }, object({
        brandID: number().required('Brand must be provided'),
        skuName: string().required('SKU Name must be provided')
    }))

    const handleClose = () => {
        createSkuDrawer.value = null
        skuFormData.value = {}
        createNewLine.value = false
    }

    const handleSubmit = async () => {
        if (!isEmpty(validatorData.errors)) return

        isSubmitting.value = true
        submitError.value = null

        try {
            const payload = {
                sku: {
                    name: skuFormData.value.skuName,
                    id_brand: skuFormData.value.brandID,
                    id_line: skuFormData.value.lineID || null,
                    id_category: skuFormData.value.categoryID || null,
                    ean: skuFormData.value.ean || null,
                    reference: skuFormData.value.reference || null,
                    new_line_name: createNewLine.value ? skuFormData.value.lineName : null,
                    size: skuFormData.value.size ? parseFloat(skuFormData.value.size) : null,
                    unity_size: skuFormData.value.unitySize || null,
                    color: skuFormData.value.color || null,
                    gender: skuFormData.value.gender || null
                },
                candidates: candidates.map(c => ({
                    id_mapping: c.id_mapping,
                    id_site: c.id_site,
                    product_id: c.product_id
                })),
                customer_ids: skuFormData.value.customerIDs || []
            }

            const res = await fetch(`${BACKEND_APIS.SKU_CANDIDATES}/create-sku`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload)
            })

            const response = await res.json()

            if (!res.ok || response.error) {
                submitError.value = response.error || `Server error: ${res.status}`
                return
            }

            if (response.sku) {
                // Remove mapped candidates from table data
                const mappedIds = new Set(candidates.map(c => c.id_mapping))
                tableData.value = tableData.value.filter(item => !mappedIds.has(item.id_mapping))
                selectedRows.value = []
                handleClose()
            } else {
                submitError.value = 'Unexpected response from server'
            }
        } catch (err) {
            submitError.value = err.message || 'An error occurred while creating the SKU'
        } finally {
            isSubmitting.value = false
        }
    }

    if (!isOpen) return null

    return (
        <DrawerSeeker
            isOpen={isOpen}
            controller={handleClose}
            title="Create New SKU"
            width="45%"
            successProps={{
                onClick: isEmpty(validatorData.errors) ? handleSubmit : null,
                disabled: isSubmitting.value
            }}
        >
            <Box>
                {submitError.value && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => submitError.value = null}>
                        {submitError.value}
                    </Alert>
                )}

                {/* Selected Products Preview */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Selected Products ({candidates.length})
                    </Typography>
                    <Box sx={{
                        maxHeight: 150,
                        overflow: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: 1,
                        p: 1
                    }}>
                        {candidates.map((candidate, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 0.5,
                                    borderBottom: index < candidates.length - 1 ? '1px solid #f3f4f6' : 'none'
                                }}
                            >
                                <Avatar
                                    src={candidate.raw?.image}
                                    variant="rounded"
                                    sx={{ width: 32, height: 32, bgcolor: '#f3f4f6' }}
                                >
                                    <IconSeeker icon="image" sx={{ fontSize: 14, color: '#9ca3af' }} />
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500 }} noWrap>
                                        {candidate.raw?.title || candidate.product_id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                        {candidate.raw?.site} - {candidate.product_id}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* SKU Form */}
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <AutocompleteAsyncSeeker
                            urlAjax={`${BACKEND_APIS.UNMATCHED}/new-products/brand`}
                            label="Brand"
                            placeholder="Search Brand..."
                            error={validatorData.errors?.brandID}
                            value={skuFormData.value.brandID}
                            onChange={e => skuFormData.value = { ...skuFormData.value, brandID: e.target.value, lineID: null }}
                        />
                    </Grid>

                    <Grid container item xs={12} sx={{ alignItems: 'center' }}>
                        <Grid item xs={11}>
                            <AutocompleteAsyncSeeker
                                urlAjax={`${BACKEND_APIS.UNMATCHED}/new-products/line`}
                                label="Line"
                                placeholder="Search Line..."
                                disabled={createNewLine.value || !skuFormData.value.brandID}
                                value={skuFormData.value.lineID}
                                onChange={e => skuFormData.value = { ...skuFormData.value, lineID: e.target.value }}
                                filters={{ brand: skuFormData.value.brandID }}
                            />
                        </Grid>
                        <Grid item xs={1}>
                            <IconSeeker
                                icon={!createNewLine.value ? 'octagon-plus' : 'square-minus'}
                                tooltip={!createNewLine.value ? 'Create New Line' : 'Hide New Line'}
                                sx={{ ml: 2.5, color: 'primary.main', fontSize: '1.5rem', cursor: 'pointer', '&:hover': { color: 'primary.dark' } }}
                                onClick={() => createNewLine.value = !createNewLine.value}
                            />
                        </Grid>
                    </Grid>

                    {createNewLine.value && (
                        <Grid item xs={12}>
                            <TextFieldSeeker
                                fullWidth
                                label="New Line Name"
                                value={skuFormData.value.lineName}
                                onChange={e => skuFormData.value = { ...skuFormData.value, lineName: e.target.value }}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TextFieldSeeker
                            fullWidth
                            label="SKU Name"
                            error={validatorData.errors?.skuName}
                            value={skuFormData.value.skuName}
                            required
                            onChange={e => skuFormData.value = { ...skuFormData.value, skuName: e.target.value }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextFieldSeeker
                            fullWidth
                            label="EAN"
                            value={skuFormData.value.ean}
                            onChange={e => skuFormData.value = { ...skuFormData.value, ean: e.target.value }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextFieldSeeker
                            fullWidth
                            label="Reference"
                            value={skuFormData.value.reference}
                            onChange={e => skuFormData.value = { ...skuFormData.value, reference: e.target.value }}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <TextFieldSeeker
                            fullWidth
                            label="Size"
                            type="number"
                            value={skuFormData.value.size}
                            onChange={e => skuFormData.value = { ...skuFormData.value, size: e.target.value }}
                        />
                    </Grid>

                    <Grid item xs={2}>
                        <TextFieldSeeker
                            fullWidth
                            label="Unit"
                            placeholder="ml, g..."
                            value={skuFormData.value.unitySize}
                            onChange={e => skuFormData.value = { ...skuFormData.value, unitySize: e.target.value }}
                            inputProps={{ maxLength: 5 }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextFieldSeeker
                            fullWidth
                            label="Color"
                            value={skuFormData.value.color}
                            onChange={e => skuFormData.value = { ...skuFormData.value, color: e.target.value }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <SelectSeeker
                            options={[
                                { label: "Female", value: 1 },
                                { label: "Male", value: 2 },
                                { label: "Unisex", value: 3 }
                            ]}
                            isClearable
                            label="Gender"
                            value={skuFormData.value.gender || ''}
                            onChange={e => skuFormData.value = { ...skuFormData.value, gender: e.target.value }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <AutocompleteAsyncSeeker
                            urlAjax={`${BACKEND_APIS.UNMATCHED}/new-products/category`}
                            label="Category"
                            placeholder="Search Category..."
                            value={skuFormData.value.categoryID}
                            onChange={e => skuFormData.value = { ...skuFormData.value, categoryID: e.target.value }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AutocompleteAsyncSeeker
                            multiple
                            urlAjax={`${BACKEND_APIS.UNMATCHED}/new-products/customer`}
                            label="Customers"
                            placeholder="Select customers to add SKU..."
                            value={skuFormData.value.customerIDs}
                            onChange={e => skuFormData.value = { ...skuFormData.value, customerIDs: e.target.value }}
                        />
                    </Grid>
                </Grid>

                {/* Info box */}
                <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="caption">
                        This will create a new SKU and map {candidates.length} product{candidates.length !== 1 ? 's' : ''} to it.
                        {skuFormData.value.customerIDs?.length > 0 && (
                            <> The SKU will be added to {skuFormData.value.customerIDs.length} customer{skuFormData.value.customerIDs.length !== 1 ? 's' : ''}.</>
                        )}
                    </Typography>
                </Alert>
            </Box>
        </DrawerSeeker>
    )
}
